import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppLayout } from '../../components/layout';
import { ChatSidebar, ChatArea } from '../../components/chat';
import type { ChatItem, AssistantFolder } from '../../components/chat/ChatSidebar/ChatSidebar';
import type { ModelResponse } from '../../components/chat/MultiModelComparison';
import { useApp } from '../../context';
import { useToast } from '../../components/ui';
import { api, Message, Chat, Assistant as ApiAssistant, MultiModelStreamChunk } from '../../services/api';

// Message type for the UI
interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt?: string;
}

// Assistant for sidebar display
interface SidebarAssistant {
  id: string;
  name: string;
  icon: string;
}

export const ChatsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state, setLLMModel } = useApp();
  const { showToast } = useToast();
  
  // Chat state
  const [ungroupedChats, setUngroupedChats] = useState<ChatItem[]>([]);
  const [assistantFolders, setAssistantFolders] = useState<AssistantFolder[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<string | undefined>();
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | undefined>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [cancelStream, setCancelStream] = useState<(() => void) | null>(null);
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  
  // Multi-model comparison state
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [compareModels, setCompareModels] = useState<string[]>([]);
  const [multiModelResponses, setMultiModelResponses] = useState<Record<string, ModelResponse>>({});
  const [isComparing, setIsComparing] = useState(false);
  const [selectedCompareModel, setSelectedCompareModel] = useState<string | null>(null);
  
  // Ref to skip auto-loading messages when we just created a new chat via send
  const skipNextMessageLoad = React.useRef(false);
  
  // Assistants for sidebar (from API)
  const [assistants, setAssistants] = useState<SidebarAssistant[]>([]);
  const [selectedAssistantDetails, setSelectedAssistantDetails] = useState<any>(null);

  // Check for assistant query parameter on mount
  useEffect(() => {
    const assistantIdFromUrl = searchParams.get('assistant');
    if (assistantIdFromUrl) {
      setSelectedAssistantId(assistantIdFromUrl);
      // Clear the URL parameter after reading it
      navigate('/', { replace: true });
    }
  }, [searchParams, navigate]);

  // Load assistants on mount, then load grouped chats
  useEffect(() => {
    const initData = async () => {
      // Load assistants first
      const assistantsLoaded = await loadAssistants();
      // Then load grouped chats (with assistants for folder names)
      loadGroupedChats(assistantsLoaded);
    };
    initData();
  }, []);

  // Load assistant details when assistant is selected
  useEffect(() => {
    if (selectedAssistantId) {
      loadAssistantDetails(selectedAssistantId);
    } else {
      setSelectedAssistantDetails(null);
    }
  }, [selectedAssistantId]);

  const loadAssistants = async (): Promise<SidebarAssistant[]> => {
    try {
      const response = await api.listAssistants();
      if (response.success) {
        const sidebarAssistants: SidebarAssistant[] = response.data.map((a: any) => ({
          id: a.id,
          name: a.name,
          icon: a.icon || '🤖',
        }));
        setAssistants(sidebarAssistants);
        return sidebarAssistants;
      }
    } catch (error) {
      console.error('Failed to load assistants:', error);
    }
    return [];
  };

  const loadAssistantDetails = async (assistantId: string) => {
    try {
      const response = await api.getAssistant(assistantId);
      if (response.success) {
        setSelectedAssistantDetails(response.data);
        // Auto-set the LLM model from assistant if available
        if (response.data.llmModel) {
          const availableModelIds = new Set(state.availableLLMModels.map(m => m.id));
          if (availableModelIds.has(response.data.llmModel)) {
            setLLMModel(response.data.llmModel);
          } else {
            setLLMModel('assistant-default');
          }
        }
      }
    } catch (error) {
      console.error('Failed to load assistant details:', error);
    }
  };

  // Load messages when chat is selected
  useEffect(() => {
    if (selectedChatId) {
      // Skip loading if we just created this chat (messages are already in state)
      if (skipNextMessageLoad.current) {
        skipNextMessageLoad.current = false;
        return;
      }
      loadMessages(selectedChatId);
    } else {
      setMessages([]);
    }
  }, [selectedChatId]);

  const loadGroupedChats = async (loadedAssistants?: SidebarAssistant[]) => {
    try {
      const response = await api.getGroupedChats();
      if (response.success) {
        // Use passed assistants or fall back to current state
        const assistantList = loadedAssistants || assistants;
        
        // Set ungrouped chats
        const ungrouped: ChatItem[] = (response.data.ungrouped || []).map((chat: Chat) => ({
          id: chat.id,
          title: chat.title || 'New Chat',
          assistantId: chat.assistantId,
          isPinned: (chat as any).isPinned || false,
          lastMessageAt: chat.updatedAt as string,
        }));
        setUngroupedChats(ungrouped);

        // Set assistant folders
        const folders: AssistantFolder[] = Object.entries(response.data.assistantChats || {}).map(
          ([assistantId, data]: [string, any]) => {
            // Prefer assistant name from API response, then from loaded assistants, then fallback
            const assistantName = data.assistantName || 
              assistantList.find(a => a.id === assistantId)?.name || 
              `Assistant ${assistantId.substring(0, 8)}...`;
            
            return {
              id: assistantId,
              name: assistantName,
              chats: (data.chats || []).map((chat: Chat) => ({
                id: chat.id,
                title: chat.title || 'New Chat',
                assistantId: chat.assistantId,
                isPinned: (chat as any).isPinned || false,
                lastMessageAt: chat.updatedAt as string,
              })),
            };
          }
        );
        setAssistantFolders(folders);
      }
    } catch (error) {
      console.error('Failed to load grouped chats:', error);
    }
  };

  const loadMessages = async (chatId: string) => {
    try {
      const response = await api.getMessages(chatId);
      if (response.success) {
        setMessages(response.data.map((m: Message) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          createdAt: m.createdAt,
        })));
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleNewChat = () => {
    setSelectedChatId(undefined);
    setSelectedAssistantId(undefined);
    setMessages([]);
  };

  const handleChatSelect = (chatId: string) => {
    setSelectedChatId(chatId);
  };

  const handleAssistantSelect = (assistantId: string) => {
    setSelectedAssistantId(assistantId);
    setSelectedChatId(undefined);
    setMessages([]);
  };

  const handleExploreAssistants = () => {
    navigate('/assistants');
  };

  const handleExploreWorkflows = () => {
    navigate('/workflows');
  };

  const handleRenameChat = async (chatId: string, newTitle: string) => {
    try {
      const response = await api.renameChat(chatId, newTitle);
      if (response.success) {
        // Reload chats to reflect changes
        await loadGroupedChats();
        showToast({
          type: 'success',
          title: 'Chat renamed',
          message: `Chat renamed to "${newTitle}"`,
        });
      }
    } catch (error) {
      console.error('Failed to rename chat:', error);
      showToast({
        type: 'error',
        title: 'Failed to rename',
        message: 'Could not rename the chat. Please try again.',
      });
    }
  };

  const handleDeleteChat = async (chatId: string) => {
    try {
      const response = await api.deleteChat(chatId);
      if (response.success) {
        // If deleted chat was selected, clear selection
        if (selectedChatId === chatId) {
          setSelectedChatId(undefined);
          setMessages([]);
        }
        // Reload chats to reflect changes
        await loadGroupedChats();
        showToast({
          type: 'success',
          title: 'Chat deleted',
          message: 'The chat has been permanently deleted.',
        });
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
      showToast({
        type: 'error',
        title: 'Failed to delete',
        message: 'Could not delete the chat. Please try again.',
      });
    }
  };

  const handlePinChat = async (chatId: string) => {
    try {
      const response = await api.togglePinChat(chatId);
      if (response.success) {
        // Reload chats to reflect changes
        await loadGroupedChats();
        const isPinned = response.data.isPinned;
        showToast({
          type: 'success',
          title: isPinned ? 'Chat pinned' : 'Chat unpinned',
          message: isPinned ? 'Chat has been pinned to the top.' : 'Chat has been unpinned.',
        });
      }
    } catch (error) {
      console.error('Failed to pin chat:', error);
      showToast({
        type: 'error',
        title: 'Failed to update',
        message: 'Could not update the pin status. Please try again.',
      });
    }
  };

  const handleSendMessage = useCallback(async (content: string, skillIds?: string[]) => {
    if (!content.trim() || isLoading || isStreaming || isComparing) return;

    // If compare mode is active with 2+ models, use multi-model flow
    if (isCompareMode && compareModels.length >= 2) {
      return handleMultiModelSend(content, skillIds);
    }

    // Show loading state immediately
    setIsLoading(true);

    let chatId = selectedChatId;

    // Create new chat if needed
    if (!chatId) {
      try {
        // Set flag so useEffect doesn't overwrite our messages
        skipNextMessageLoad.current = true;
        const response = await api.createChat(selectedAssistantId);
        if (response.success) {
          chatId = response.data.id;
          setSelectedChatId(chatId);
          // Reload grouped chats to show the new chat in the right place
          loadGroupedChats();
        } else {
          console.error('Failed to create chat');
          setIsLoading(false);
          return;
        }
      } catch (error) {
        console.error('Failed to create chat:', error);
        setIsLoading(false);
        return;
      }
    }

    // Add user message to UI immediately
    const userMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: content,
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Start streaming
    setStreamingContent('');
    setToolStatus(null);

    // Validate selected model against currently available models to avoid stale/invalid model IDs.
    const preferredModel = state.selectedLLMModel !== 'assistant-default'
      ? state.selectedLLMModel
      : 'gpt-4o';
    const availableModelIds = new Set(state.availableLLMModels.map(m => m.id));
    const fallbackModel = state.availableLLMModels.find(m => m.id !== 'assistant-default')?.id || 'gpt-4o';
    const selectedModel = availableModelIds.has(preferredModel) ? preferredModel : fallbackModel;

    if (selectedModel !== preferredModel) {
      setLLMModel(selectedModel);
      showToast({
        type: 'warning',
        title: 'Model switched',
        message: `"${preferredModel}" is unavailable. Switched to "${selectedModel}".`,
      });
    }

    const cancel = api.streamMessage(
      chatId,
      content,
      (chunk) => {
        if (chunk.type === 'content' && chunk.data) {
          // Transition from loading to streaming on first content
          setIsLoading(false);
          setIsStreaming(true);
          setToolStatus(null);
          setStreamingContent(prev => prev + chunk.data);
        } else if (chunk.type === 'tool_call' && chunk.data) {
          // Show tool execution status to the user
          try {
            const toolData = JSON.parse(chunk.data);
            if (toolData.status === 'executed' && toolData.name) {
              // Tool finished executing — show briefly
              const friendlyName = toolData.name.replace(/_/g, ' ').replace(/^\w/, (c: string) => c.toUpperCase());
              setToolStatus(`✅ ${friendlyName} completed`);
            } else if (toolData.function?.name && toolData.id) {
              // Tool call initiated — show which tool is being called
              const friendlyName = toolData.function.name.replace(/_/g, ' ').replace(/^\w/, (c: string) => c.toUpperCase());
              setToolStatus(`🔧 Calling ${friendlyName}...`);
            }
          } catch {
            // Ignore parse errors for incremental tool_call chunks
          }
        } else if (chunk.type === 'done') {
          // Finalize the message
          setIsLoading(false);
          setIsStreaming(false);
          setToolStatus(null);
          setStreamingContent(prev => {
            if (prev) {
              const assistantMessage: ChatMessage = {
                id: `msg-${Date.now()}`,
                role: 'assistant',
                content: prev,
                createdAt: new Date().toISOString(),
              };
              setMessages(msgs => [...msgs, assistantMessage]);
            }
            return '';
          });
          // Reload grouped chats to update chat title in sidebar
          loadGroupedChats();
        } else if (chunk.type === 'error') {
          setIsLoading(false);
          setIsStreaming(false);
          setStreamingContent('');
          setToolStatus(null);
          // Add error message
          const errorMessage: ChatMessage = {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: chunk.error || 'An error occurred. Please try again.',
            createdAt: new Date().toISOString(),
          };
          setMessages(msgs => [...msgs, errorMessage]);
        }
      },
      (error) => {
        console.error('Stream error:', error);
        setIsLoading(false);
        setIsStreaming(false);
        setStreamingContent('');
        setToolStatus(null);
        // Show error message to user (only if onChunk didn't already handle it)
        setMessages(prev => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg?.role === 'assistant' && lastMsg.content.includes('error')) {
            return prev; // Already handled by onChunk error
          }
          return [...prev, {
            id: `error-${Date.now()}`,
            role: 'assistant' as const,
            content: `⚠️ ${error.message || 'Connection failed. Please try again.'}`,
            createdAt: new Date().toISOString(),
          }];
        });
      },
      selectedModel,  // Pass the selected model
      skillIds  // Pass selected skills
    );

    setCancelStream(() => cancel);
  }, [
    selectedChatId,
    selectedAssistantId,
    isLoading,
    isStreaming,
    isComparing,
    isCompareMode,
    compareModels,
    state.selectedLLMModel,
    state.availableLLMModels,
    setLLMModel,
    showToast,
  ]);

  /**
   * Multi-model comparison flow: fan out the same prompt to multiple models
   */
  const handleMultiModelSend = useCallback(async (content: string, skillIds?: string[]) => {
    setIsLoading(true);

    let chatId = selectedChatId;

    // Create new chat if needed
    if (!chatId) {
      try {
        skipNextMessageLoad.current = true;
        const response = await api.createChat(selectedAssistantId);
        if (response.success) {
          chatId = response.data.id;
          setSelectedChatId(chatId);
          loadGroupedChats();
        } else {
          setIsLoading(false);
          return;
        }
      } catch (error) {
        console.error('Failed to create chat for multi-model:', error);
        setIsLoading(false);
        return;
      }
    }

    // Add user message
    const userMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Initialize multi-model response state
    const initialResponses: Record<string, ModelResponse> = {};
    compareModels.forEach(model => {
      initialResponses[model] = {
        model,
        content: '',
        status: 'streaming',
      };
    });
    setMultiModelResponses(initialResponses);
    setSelectedCompareModel(null);
    setIsComparing(true);
    setIsLoading(false);

    const cancel = api.multiModelStream(
      chatId,
      content,
      compareModels,
      (chunk: MultiModelStreamChunk) => {
        if (chunk.type === 'content' && chunk.data && chunk.model) {
          setMultiModelResponses(prev => ({
            ...prev,
            [chunk.model]: {
              ...prev[chunk.model],
              content: (prev[chunk.model]?.content || '') + chunk.data,
              status: 'streaming',
            },
          }));
        } else if (chunk.type === 'model_done' && chunk.model) {
          setMultiModelResponses(prev => ({
            ...prev,
            [chunk.model]: {
              ...prev[chunk.model],
              status: 'completed',
              latencyMs: chunk.latencyMs,
            },
          }));
        } else if (chunk.type === 'error' && chunk.model) {
          setMultiModelResponses(prev => ({
            ...prev,
            [chunk.model]: {
              ...prev[chunk.model],
              status: 'error',
              error: chunk.error || 'Model error',
            },
          }));
        } else if (chunk.type === 'all_done') {
          setIsComparing(false);
          loadGroupedChats();
        }
      },
      (error) => {
        console.error('Multi-stream error:', error);
        setIsComparing(false);
        showToast({
          type: 'error',
          title: 'Comparison failed',
          message: error.message || 'Multi-model comparison encountered an error.',
        });
      },
      skillIds
    );

    setCancelStream(() => cancel);
  }, [selectedChatId, selectedAssistantId, compareModels]);

  /**
   * Called when the user selects their preferred model response
   */
  const handleSelectCompareModel = useCallback(async (model: string) => {
    if (!selectedChatId) return;

    const response = multiModelResponses[model];
    if (!response || !response.content) return;

    setSelectedCompareModel(model);

    try {
      // Save the selected response as the official assistant message
      const result = await api.finalizeModelResponse(
        selectedChatId,
        model,
        response.content
      );

      if (result.success) {
        // Add the finalized message to the chat
        const assistantMessage: ChatMessage = {
          id: result.data.assistantMessage.id || `msg-${Date.now()}`,
          role: 'assistant',
          content: response.content,
          createdAt: new Date().toISOString(),
        };
        setMessages(prev => [...prev, assistantMessage]);

        showToast({
          type: 'success',
          title: 'Response selected',
          message: `Selected ${model} response. Continuing conversation.`,
        });

        // Clear multi-model state after a brief delay to let the user see the selection
        setTimeout(() => {
          setMultiModelResponses({});
          setSelectedCompareModel(null);
          setIsCompareMode(false);
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to finalize model response:', error);
      showToast({
        type: 'error',
        title: 'Selection failed',
        message: 'Could not save the selected response. Please try again.',
      });
      setSelectedCompareModel(null);
    }
  }, [selectedChatId, multiModelResponses]);

  const handleCancelStream = useCallback(() => {
    if (cancelStream) {
      cancelStream();
      setIsStreaming(false);
      // Keep whatever content we have so far
      if (streamingContent) {
        const assistantMessage: ChatMessage = {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content: streamingContent + ' [Cancelled]',
          createdAt: new Date().toISOString(),
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
      setStreamingContent('');
    }
    // Also clear multi-model comparison state if active
    if (isComparing) {
      setIsComparing(false);
      setMultiModelResponses({});
      setSelectedCompareModel(null);
    }
  }, [cancelStream, streamingContent, isComparing]);

  const handleStarterClick = (starter: { id: string; text: string }) => {
    handleSendMessage(starter.text);
  };

  // Get assistant details for ChatArea
  const selectedAssistant = selectedAssistantDetails
    ? {
        id: selectedAssistantDetails.id,
        name: selectedAssistantDetails.name,
        conversationStarters: (selectedAssistantDetails.conversationStarters || []).map(
          (text: string, index: number) => ({ id: String(index), text })
        ),
      }
    : undefined;

  const sidebar = (
    <ChatSidebar
      assistants={assistants}
      ungroupedChats={ungroupedChats}
      assistantFolders={assistantFolders}
      selectedChatId={selectedChatId}
      selectedAssistantId={selectedAssistantId}
      onNewChat={handleNewChat}
      onChatSelect={handleChatSelect}
      onAssistantSelect={handleAssistantSelect}
      onExploreAssistants={handleExploreAssistants}
      onExploreWorkflows={handleExploreWorkflows}
      onRenameChat={handleRenameChat}
      onDeleteChat={handleDeleteChat}
      onPinChat={handlePinChat}
    />
  );

  return (
    <AppLayout sidebar={sidebar}>
      <ChatArea
        selectedAssistant={selectedAssistant}
        messages={messages}
        streamingContent={streamingContent}
        isStreaming={isStreaming}
        isLoading={isLoading}
        toolStatus={toolStatus}
        onSendMessage={handleSendMessage}
        onCancelStream={handleCancelStream}
        onStarterClick={handleStarterClick}
        isNewChat={!selectedChatId && messages.length === 0}
        selectedModel={state.selectedLLMModel}
        onModelChange={setLLMModel}
        isCompareMode={isCompareMode}
        onCompareModeChange={setIsCompareMode}
        compareModels={compareModels}
        onCompareModelsChange={setCompareModels}
        multiModelResponses={multiModelResponses}
        isComparing={isComparing}
        selectedCompareModel={selectedCompareModel}
        onSelectCompareModel={handleSelectCompareModel}
      />
    </AppLayout>
  );
};

export default ChatsPage;
